import React from 'react';
import { FiLoader } from 'react-icons/fi';

import type { QuickInferenceFileSummary } from '../../../helpers/api/quick-inference/quick-inference';
import { fileKindFromMeta, truncateFilename } from './quickInferenceUtils';
import { QuickInferenceMediaThumb } from './QuickInferenceMediaThumb';

interface Props {
  files: QuickInferenceFileSummary[];
  selectedFileId: string | null;
  onSelect: (fileId: string) => void;
}

export function QuickInferenceViewerFileList({ files, selectedFileId, onSelect }: Props) {
  return (
    <div className="space-y-1.5">
      {files.map((file, index) => {
        const selected = file.id === selectedFileId;
        const kind = fileKindFromMeta(file.content_type, file.filename, file.pacs_ingest_status);
        const isPending = file.status === 'pending';

        return (
          <button
            key={file.id}
            type="button"
            onClick={() => onSelect(file.id)}
            className={`group w-full rounded-xl border text-left transition-all duration-200 focus:outline-none ${
              selected
                ? 'border-blue-300 bg-blue-50 shadow-sm dark:border-blue-500/50 dark:bg-blue-900/20'
                : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-800/40 dark:hover:border-gray-700 dark:hover:bg-gray-800'
            }`}
          >
            <div className="relative mx-2 mt-2 aspect-[4/3] overflow-hidden rounded-lg">
              <QuickInferenceMediaThumb
                inferenceId={file.id}
                status={file.status}
                kind={kind}
                iconSize={18}
                className="absolute inset-0 h-full w-full"
              />
              <span className="absolute left-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded bg-black/60 px-1 text-[9px] font-bold text-white">
                {index + 1}
              </span>
            </div>
            <div className="px-2.5 pb-2 pt-1.5">
              <p
                className={`truncate text-[10px] font-semibold ${
                  selected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'
                }`}
                title={file.filename}
              >
                {truncateFilename(file.filename, 28)}
              </p>
              <p className="mt-0.5 text-[9px] text-gray-400">
                {file.number_of_frames} frame{file.number_of_frames === 1 ? '' : 's'}
              </p>
              {selected ? (
                <div className="mt-1 flex items-center gap-1">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
                  </span>
                  <span className="text-[9px] font-semibold text-green-600 dark:text-green-400">
                    Viewing
                  </span>
                </div>
              ) : isPending ? (
                <div className="mt-1 flex items-center gap-1 text-[9px] text-blue-500">
                  <FiLoader size={9} className="animate-spin" />
                  Processing
                </div>
              ) : (
                <p className="mt-0.5 text-[9px] capitalize text-gray-400">{file.status}</p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
